import { useState, useEffect } from 'react'
import { Layout } from 'components/Layout'
import { CurrentUserContextProvider } from 'context/useCurrentUser'
import { NotificationProvider } from 'context'
import {
  Container,
  Title,
  Paper,
  LoadingOverlay,
  Alert,
  Divider,
} from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { SchoolFeaturesList } from './components/SchoolFeaturesList'
import { AdminProtectedWrapper } from './components/AdminProtectedWrapper'
import { featureFlagsService } from './services/featureFlagsService'
import { ISchool, IFeature } from './types'

export const FeatureFlags = () => {
  const { t } = useTranslation()
  const [schools, setSchools] = useState<ISchool[]>([])
  const [globalFeatures, setGlobalFeatures] = useState<IFeature[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [schoolsData, featuresData] = await Promise.all([
        featureFlagsService.getSchools(),
        featureFlagsService.getGlobalFeatures(),
      ])
      setSchools(schoolsData)
      setGlobalFeatures(featuresData)
    } catch (err) {
      setError(t('feature_flags_error_loading'))
      console.error('Error loading data:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleAddFeatures = async (schoolId: string, features: string[]) => {
    try {
      await featureFlagsService.addSchoolFeatures({
        school_id: schoolId,
        features,
      })
      await loadData()
    } catch (err) {
      console.error('Error adding school features:', err)
      throw err
    }
  }

  const handleRemoveFeatures = async (schoolId: string, features: string[]) => {
    try {
      await featureFlagsService.removeSchoolFeatures({
        school_id: schoolId,
        features,
      })
      await loadData()
    } catch (err) {
      console.error('Error removing school features:', err)
      throw err
    }
  }

  return (
    <CurrentUserContextProvider>
      <NotificationProvider>
        <Layout>
          <AdminProtectedWrapper>
            <Container size="xl" py="xl">
              <Title order={1} mb="xl">
                {t('feature_flags_page_title')}
              </Title>

              <Divider my="sm" />

              {error && (
                <Alert color="red" mb="md">
                  {error}
                </Alert>
              )}

              <Paper shadow="sm" p="md" pos="relative">
                <LoadingOverlay visible={loading} />

                <SchoolFeaturesList
                  schools={schools}
                  globalFeatures={globalFeatures}
                  onAddFeatures={handleAddFeatures}
                  onRemoveFeatures={handleRemoveFeatures}
                  onReload={loadData}
                />
              </Paper>
            </Container>
          </AdminProtectedWrapper>
        </Layout>
      </NotificationProvider>
    </CurrentUserContextProvider>
  )
}
